import { FTProcess } from '@/models/FTProcess';
import { weightToLength, initProcess, ProcessConfig, ConversionResults, ParsedVariables } from './index'
import { ConversionDetails } from '@/models/ConversionDetails';
import { FTProcessRow } from '@/models/FTProcessRow';
import _ from 'lodash';

export function loadConfig({ process, processRow }: ParsedVariables): ProcessConfig {
  return {
    settings: {
      fluteType: {
        name: 'Flute Type',
        type: 'dropdown',
        optionsArray: [ 'E-Flute', 'F-Flute' ],
        default: 'E-Flute'
      },
      slittingEnabled: {
        type: 'checkbox',
        name: 'Enable Slitting',
      }
    },
    inputs: {
      goods: [
        {
          goodsType: 'corrugated-input',
          goodsFilter: {
            form: ['roll']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'liner-input',
          goodsFilter: {
            form: ['roll']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'adhesive',
          goodsFilter: {
            type: ['ORM'],
            family: ['Chemical'],
            category: ['Adhesive']
          },
          multiple: false,
          params: [
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'multiple',
      enableMultipleIf: _.get(process, 'settings.slittingEnabled') === true,
      arguments: [
        {
          name: 'Width',
          type: 'dimension',
          value: 'width',
          unit: 'mm',
          disabled: false
        },
        {
          name: 'Output Quantity',
          type: 'quantity',
          value: 'quantity',
          disabled: false
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'Linear Length',
        value: 'linearMeters'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);
  conversionResult.outputs = process.outputs;

  const corrugatingInputFTProcessRow = process.inputs[0];
  const linerInputFTProcessRow = process.inputs[1];
  const adhesiveFTProcessRow = process.inputs[2];
  const outputFTProcessRow = process.outputs[0];

  const linerQuantity = linerInputFTProcessRow.quantity ? linerInputFTProcessRow.quantity : 0;
  const corrugatingQuantity = corrugatingInputFTProcessRow.quantity ? corrugatingInputFTProcessRow.quantity : 0;
  const adhesiveQuantity = adhesiveFTProcessRow.quantity ? adhesiveFTProcessRow.quantity : 0;
  const totalInputQuantity = linerQuantity + corrugatingQuantity + adhesiveQuantity;

  let linerInputWidth = 0;
  let linerDensity = 0;
  let netWeight = 0;
  const takeUpRatio = process.settings.fluteType == 'E-Flute' ? 1.3 : 1.21;
  if (corrugatingInputFTProcessRow.goodsObject && linerInputFTProcessRow.goodsObject) {

    linerInputWidth = linerInputFTProcessRow.goodsObject.dimensions ? linerInputFTProcessRow.goodsObject.dimensions.width : 0;

    linerDensity = linerInputFTProcessRow.goodsObject.density ? linerInputFTProcessRow.goodsObject.density : 0;
    const corrugatingDensity = corrugatingInputFTProcessRow.goodsObject && corrugatingInputFTProcessRow.goodsObject.density ? corrugatingInputFTProcessRow.goodsObject.density : 0;
    const outputDensity = linerDensity + corrugatingDensity * takeUpRatio + 5;

    process.outputs = _.map(process.outputs, (outputFTProcessRow: FTProcessRow) => {
      outputFTProcessRow.goodsObject.copyFrom(corrugatingInputFTProcessRow.goodsObject);
      outputFTProcessRow.goodsObject.density = outputDensity;
      outputFTProcessRow.goodsObject.setPerKgRatio('plB', 1);
      netWeight = netWeight + outputFTProcessRow.quantity;
      if (outputFTProcessRow.goodsObject.dimensions) {
        outputFTProcessRow.goodsObject.dimensions.width = outputFTProcessRow.dimensions.width
      }
      const baseUnit = _.get(process, 'ftFactoryTicket.order.product.packingMode.0.unit1');
      if(baseUnit == 'kg') {
        outputFTProcessRow.goodsObject.setPerKgRatio('plA', 1);
      }
      if(baseUnit == 'lkm') {
        const linearLength = weightToLength(outputFTProcessRow.quantity, outputFTProcessRow.goodsObject.density, _.get(outputFTProcessRow, 'goodsObject.dimensions.width', 0));
        const perKgRatio = (linearLength/1000)/outputFTProcessRow.quantity || 0;
        outputFTProcessRow.goodsObject.setPerKgRatio('plA', perKgRatio);  
      }
      return outputFTProcessRow;
    })
  }
  if(process.settings.slittingEnabled) {
    _.each(process.outputs, (outputFTProcessRow: FTProcessRow) => {
      const rollRatio = ((outputFTProcessRow.quantity / netWeight) * linerInputWidth) / outputFTProcessRow.dimensions.width;
      outputFTProcessRow.goodsObject.setPerKgRatio('plB', Math.floor(rollRatio));

    })
  }
  conversionResult.details.wastePercentage = ((totalInputQuantity - netWeight) / totalInputQuantity) * 100;
  conversionResult.details.waste = totalInputQuantity - netWeight;
  conversionResult.details.linearMeters = weightToLength(totalInputQuantity, linerDensity, linerInputWidth);
  conversionResult.details.netWeight = netWeight;
  conversionResult.details.kgPerHour = 60 * (process.machineDetail.mechanicalSpeed / conversionResult.details.linearMeters) * outputFTProcessRow.quantity;
  conversionResult.details.plannedHours = conversionResult.details.linearMeters / (process.machineDetail.mechanicalSpeed * 60);

  return conversionResult;
}